import React, { useState, useEffect, useMemo, useCallback } from "react";
import { IconSearch } from "@tabler/icons-react";
import UserTable from "./UserTable";
import { useTranslation } from "react-i18next";

const ActionDialog = ({
  form,
  open,
  onClose,
  checkedUsers,
  handleUpdateAccountRole,
  conference,
  onChangeSearchInput,
  onChangePage,
  currentPageUsers,
  pagination,
}) => {
  const [searchTerm, setSearchTerm] = useState(form.search || "");
  const [loadedUsers, setLoadedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(checkedUsers);
  const { t: getLabel } = useTranslation();

  const hasMore = useMemo(() => pagination.total > pagination.current, [pagination]);

  useEffect(() => {
    setIsLoading(true);
    Promise.resolve(onChangePage?.(null, pagination.current)).finally(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setSearchTerm(form.search || "");
  }, [form.search]);

  useEffect(() => {
    setLoadedUsers(prevUsers => {
      const newUsers = currentPageUsers.filter(user => !prevUsers.some(prevUser => prevUser.id === user.id));
      return [...prevUsers, ...newUsers];
    });
  }, [currentPageUsers]);

  useEffect(() => {
    setSelectedUsers(checkedUsers);
  }, [checkedUsers]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    setIsLoading(true);
    Promise.resolve(onChangePage?.(null, pagination.current + 1)).finally(() => {
      setIsLoading(false);
    });
  }, [hasMore, isLoading, onChangePage, pagination.current]);

  const handleAddUser = user => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      return isSelected ? prev.filter(u => u.id !== user.id) : [...prev, user];
    });
  };

  const handleSubmit = () => {
    if (selectedUsers.length > 0) {
      handleUpdateAccountRole({
        serviceId: conference.serviceId,
        accountIds: selectedUsers.map(user => user.accountId),
      });
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return loadedUsers;
    return loadedUsers.filter(
      user => user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || user?.phone?.includes(searchTerm),
    );
  }, [loadedUsers, searchTerm]);

  const isChanged = useMemo(() => {
    const selectedIds = selectedUsers.map(u => u.id).sort();
    const checkedIds = checkedUsers.map(u => u.id).sort();
    return JSON.stringify(selectedIds) !== JSON.stringify(checkedIds);
  }, [selectedUsers, checkedUsers]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal modal-blur fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, marginLeft: 0, zIndex: 1055 }}
        onKeyDown={e => { if (e.key === "Escape") onClose(); }}
        ref={el => el && el.focus()}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {getLabel("TXT_ADD_ADMINISTRATOR")}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 16, fontWeight: "bold", marginBottom: 8 }}>
                {getLabel("TXT_TOTAL_ADMINS")}: {selectedUsers.length}
              </p>

              <div style={{ padding: "5px 10px", marginBottom: 16 }}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    defaultValue={form.search}
                    onChange={onChangeSearchInput}
                    placeholder={getLabel("P_ADD_NEW_ADMIN")}
                  />
                  <span className="input-group-text">
                    <IconSearch size={16} />
                  </span>
                </div>
              </div>
              <p style={{ fontWeight: "bold", fontSize: 14, marginBottom: 8 }}>Danh sách người dùng</p>
              <div className="d-flex gap-2">
                <UserTable
                  users={filteredUsers}
                  selectedUsers={selectedUsers}
                  onActionClick={handleAddUser}
                  actionType="add"
                  loadMore={loadMore}
                  hasMore={hasMore}
                />
              </div>

              <div style={{ marginTop: 16 }}>
                <button
                  className="btn btn-primary w-100"
                  disabled={!isChanged}
                  onClick={handleSubmit}
                >
                  {getLabel("L_UPDATE")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActionDialog;
